import React, { useMemo } from 'react'
import {
    BaseEdge,
    EdgeLabelRenderer,
    EdgeProps,
    useReactFlow,
} from '@xyflow/react'
import { useJourneyContext } from '../../context'
import { getJourneyDisplayItemType } from '../../../../_utils/journeys'
import { SPLIT_LAYOUT_CONSTANTS } from '../../layout/elk-config'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { JourneyEdge, JourneyNode } from '../../types/journey-nodes'
import { useEdgeActions } from '../shared/use-edge-actions'
import { EdgeActionPopover } from '../shared/edge-action-popover'
import { getEdgeAddButtonAnchor } from '../shared/edge-add-button-anchor'
import type { ElkEdgeSection } from 'elkjs/lib/elk-api'
import { getBranchingNodeData } from '../../utils/graph/traversal'
import { PERF_CARD_NODE_ID_PREFIX } from '../../constants'

/**
 * Build an SVG path string from ELK edge sections with rounded corners.
 *
 * At each bend point the path shortens the incoming and outgoing straight
 * segments by CORNER_RADIUS pixels and inserts a quadratic Bézier curve
 * (Q command) through the original bend point, producing a smooth arc.
 * When a segment is too short for the full radius the radius is clamped
 * so the path never overshoots.
 *
 * Exported so custom-edge can reuse it for convergence edges.
 */
export function buildPathFromSections(sections: ElkEdgeSection[]): string {
    const r = SPLIT_LAYOUT_CONSTANTS.CORNER_RADIUS
    const parts: string[] = []

    for (const section of sections) {
        const points = [
            section.startPoint,
            ...(section.bendPoints ?? []),
            section.endPoint,
        ]

        if (points.length < 2) continue

        parts.push(`M ${points[0].x} ${points[0].y}`)

        if (points.length === 2) {
            parts.push(`L ${points[1].x} ${points[1].y}`)
            continue
        }

        for (let i = 1; i < points.length - 1; i++) {
            const prev = points[i - 1]
            const curr = points[i]
            const next = points[i + 1]

            // Distances to prev and next points
            const dPrev = Math.hypot(curr.x - prev.x, curr.y - prev.y)
            const dNext = Math.hypot(next.x - curr.x, next.y - curr.y)

            // Clamp radius so we don't overshoot either segment
            const cr = Math.min(r, dPrev / 2, dNext / 2)

            if (cr < 1) {
                // Segment too short for a curve — just draw straight
                parts.push(`L ${curr.x} ${curr.y}`)
                continue
            }

            // Point where the curve starts (on the incoming segment)
            const startX = curr.x + ((prev.x - curr.x) / dPrev) * cr
            const startY = curr.y + ((prev.y - curr.y) / dPrev) * cr

            // Point where the curve ends (on the outgoing segment)
            const endX = curr.x + ((next.x - curr.x) / dNext) * cr
            const endY = curr.y + ((next.y - curr.y) / dNext) * cr

            parts.push(`L ${startX} ${startY}`)
            parts.push(`Q ${curr.x} ${curr.y} ${endX} ${endY}`)
        }

        // Final straight segment to the end point
        const last = points[points.length - 1]
        parts.push(`L ${last.x} ${last.y}`)
    }

    return parts.join(' ')
}

/**
 * Variant branch edge: split port → variant lane head.
 *
 * NS-2599 re-architecture: the variant label, the metrics card, and the (+)
 * button are NO LONGER EdgeLabelRenderer overlays. The variant's perf card is a
 * real ELK lane-head node (see perf-card-injection.ts), so the variant name +
 * percentage live in the card header (centered by ELK under the lane's turn),
 * and the on-edge add button is an ordinary one positioned by ELK routing. This
 * edge therefore renders only the path + a single add button — no floating
 * label, no overlay chip.
 */
export const BranchEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    source,
    target,
    data,
    ...props
}: EdgeProps<JourneyEdge>) => {
    const [state] = useJourneyContext()
    const { getNode } = useReactFlow<JourneyNode>()

    const branchData = data

    const { edgeActionOptions, showEdgeActions, setShowEdgeActions, handleAddNode } = useEdgeActions({
        source,
        target,
        variantId: branchData?.branchKey,
    })

    // Extract ELK-computed bend points if available
    const elkSections = branchData?.elkSections
    const hasELKRouting = elkSections && elkSections.length > 0

    // Determine branch index and count from the source branching step node
    // so we can calculate horizontal offset for each branch edge (fallback only)
    const sourceNode = getNode(source)
    const branchInfo = useMemo(() => {
        if (!sourceNode) return { index: 0, count: 1 }
        const branchingData = getBranchingNodeData(sourceNode)
        if (!branchingData) return { index: 0, count: 1 }
        const branches = branchingData.branches
        const idx = branches.findIndex(b => branchData && b.key === branchData.branchKey)
        return { index: idx >= 0 ? idx : 0, count: branches.length || 1 }
    }, [sourceNode, branchData])

    // Build edge path: prefer ELK bend points, fall back to manual construction
    const edgePath = useMemo(() => {
        if (hasELKRouting) {
            return buildPathFromSections(elkSections!)
        }

        // Fallback: manual path construction (pre-ELK behavior)
        if (process.env.NODE_ENV === 'development') {
            console.warn('BranchEdge: falling back to manual path construction for edge', id)
        }

        const LANE_SPREAD = 80
        const centerOffset = (branchInfo.count - 1) / 2
        const laneOffset = (branchInfo.index - centerOffset) * LANE_SPREAD
        const laneX = sourceX + laneOffset
        const midY = sourceY + (targetY - sourceY) * 0.35
        const convergeY = targetY - 25

        if (Math.abs(laneOffset) < 2) {
            return `M ${sourceX} ${sourceY} L ${sourceX} ${targetY}`
        }

        return [
            `M ${sourceX} ${sourceY}`,
            `L ${sourceX} ${midY}`,
            `L ${laneX} ${midY}`,
            `L ${laneX} ${convergeY}`,
            `L ${targetX} ${convergeY}`,
            `L ${targetX} ${targetY}`,
        ].join(' ')
    }, [sourceX, sourceY, targetX, targetY, hasELKRouting, elkSections, branchInfo, id])

    // --- Add button positioning ---
    // Shared shape-aware anchor (same helper custom-edge uses). The only branch
    // edge that actually renders a button is the empty-lane case (≥4 bends → lane
    // center); split → perf-card edges suppress their button below.
    const { addButtonX, addButtonY } = useMemo(
        () => getEdgeAddButtonAnchor({
            sections: elkSections,
            sourceX,
            sourceY,
            targetX,
            targetY,
        }),
        [elkSections, sourceX, sourceY, targetX, targetY],
    )

    // Hide arrow markers on edges targeting convergence dots for a cleaner look.
    const targetNode = getNode(target)
    const targetIsConvergence = targetNode?.data?.step?.type === StepType.PLACEHOLDER
        && targetNode?.data?.uiMeta?.placeholderRole === 'convergence'

    // A split → perf-card edge is just the visual connector into the variant's
    // performance card; there is nothing to insert between a split and its own
    // card. The "add the first step in this variant" affordance lives on the
    // card → first-step edge below the card (an ordinary custom edge). Rendering
    // an add button here would land it on top of the card, so suppress it.
    const targetIsPerfCard = target.startsWith(PERF_CARD_NODE_ID_PREFIX)

    return (
        <>
            <BaseEdge id={id} path={edgePath} markerEnd={targetIsConvergence ? undefined : props.markerEnd} markerStart={props.markerStart} />
            <EdgeLabelRenderer>
                {/* Add step button — ordinary on-edge button positioned by ELK routing.
                    Suppressed when this edge feeds a perf card (see above). */}
                {!targetIsPerfCard && (
                    <EdgeActionPopover
                        addButtonX={addButtonX}
                        addButtonY={addButtonY}
                        edgeActionOptions={edgeActionOptions}
                        showEdgeActions={showEdgeActions}
                        setShowEdgeActions={setShowEdgeActions}
                        handleAddNode={handleAddNode}
                        mode={state.mode}
                        itemType={getJourneyDisplayItemType(state.journey, state.domain.ecommConfig?.itemType)}
                    />
                )}
            </EdgeLabelRenderer>
        </>
    )
}
